<template>
    <v-dialog v-model="modal" persistent width="800">
        <v-card v-if="!loaded" color="primary" dark>
            <v-card-title class="justify-space-between px-4 fs-14">
                {{ services.length > 1 ? $t('book') + ' ' + $t('all') : services[0] && services[0].serviceName }}
            </v-card-title>
            <v-card-text class="fs-14">
                <slot name="info" />
                {{ $t('stand_by') }}
                <v-progress-linear indeterminate color="white" class="mb-0" />
            </v-card-text>
        </v-card>
        <v-card v-else class="v-card-modal">
            <v-card-title>
                {{ services.length > 1 ? $t('book') + ' ' + $t('all') : services[0] && services[0].serviceName }}
            </v-card-title>
            <div
                v-if="services.length === 1 && prepareBookResult.length && prepareBookResult[0].availableForBooking"
                class="mt-4 mx-4 text--secondary"
            >
                {{ $t('actual_availability') }}:
                <span :class="`text-${prepareBookResult[0].onRequest ? 'warning' : 'success'}`">
                    {{
                        prepareBookResult[0].onRequest
                            ? $t('filters_availability.on_request')
                            : $t('filters_availability.available')
                    }}
                </span>
            </div>
            <payment-options v-model="paymentMethodId" class="elevation-0" :disabled="bookingActive" />
            <payment-redirect-form ref="paymentRedirectForm" />
            <due-to-confirm-date-checkbox
                v-if="services.length === 1"
                :start-date="services[0].startDateTime"
                :due-to-confirm-date="dueToConfirmDate"
                :due-to-confirm-date-active="dueToConfirmDateActive"
                class="mx-4 mb-4"
                @change-due-to-confirm-date="dueToConfirmDate = $event"
                @change-due-to-confirm-date-active="dueToConfirmDateActive = $event"
            />
            <v-divider />
            <v-card-actions class="justify-center">
                <v-btn
                    variant="flat"
                    color="primary"
                    :disabled="bookingActive || !paymentMethodId"
                    :loading="bookingActive"
                    class="me-5"
                    @click="book"
                >
                    {{ $t('book') }}
                </v-btn>
                <v-btn variant="flat" color="white" :disabled="bookingActive" @click="modal = false">
                    {{ $t('cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <prepare-book-messages-modal
            v-if="prepareBookResponse"
            :prepare-book-response="prepareBookResponse"
            :offers-data="offersData"
            show-all
            @approve="continueBooking"
            @cancel="modal = false"
            @answers-changed="onAnswersChanged"
        >
            <template v-slot:notes>
                <div v-if="extraServices.length" class="text-center mb-2">
                    {{ $t('notes.offline_service_booking') }}
                </div>
            </template>
            <template v-slot="{availableForBooking, onRequest, serviceType}">
                <div v-if="availableForBooking && serviceType !== 'EXTRASERVICE'" class="mt-2 text--secondary">
                    {{ $t('actual_availability') }}:
                    <span :class="`text-${onRequest ? 'warning' : 'success'}`">
                        {{ onRequest ? $t('filters_availability.on_request') : $t('filters_availability.available') }}
                    </span>
                </div>
            </template>
        </prepare-book-messages-modal>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import {EventBus, ORDER_SERVICE_BOOK, ORDER_SERVICE_BOOKED} from '@/utils/event-bus'
    import {BookingMixin} from '@/components/mixins/BookingMixin.vue'
    import PaymentOptions from '@/components/booking/PaymentOptions.vue'
    import PaymentRedirectForm from '@/components/booking/forms/PaymentRedirectForm.vue'
    import PrepareBookMessagesModal from '@/components/booking/modals/PrepareBookMessagesModal.vue'
    import {prepareBookResult} from '@/utils/PrepareBookAdapter'
    import DueToConfirmDateCheckbox from '@/components/booking/forms/fields/DueToConfirmDateCheckbox.vue'
    import OfflineServiceMixin from '~src/components/account/offlineServices/mixins/OfflineServiceMixin.vue'

    @Component({
        components: {
            DueToConfirmDateCheckbox,
            PrepareBookMessagesModal,
            PaymentOptions,
            PaymentRedirectForm,
        },
    })
    export class OrderServiceBookModal extends mixins(BookingMixin) {
        services = []
        extraServices = []
        loaded = false
        modal = false
        prepareBookResponse = null
        paymentMethodId = null
        bookingActive = false
        dueToConfirmDate = null
        dueToConfirmDateActive = false
        order = null

        @Watch('modal')
        onChangeModal() {
            this.prepareBookResponse = null
        }

        mounted() {
            EventBus.$on(ORDER_SERVICE_BOOK, this.show)
        }

        beforeUnmount() {
            EventBus.$off(ORDER_SERVICE_BOOK, this.show)
        }

        async show({services, order}) {
            this.order = order
            this.services = services.filter(({serviceType}) => serviceType !== 'EXTRASERVICE')
            if (authStore.isTO1) {
                this.extraServices = services.filter(({serviceType}) => serviceType === 'EXTRASERVICE')
            }
            this.loaded = false
            this.modal = true
            await this.$nextTick()
            this.paymentMethodId = null
            this.bookingActive = false

            let prepareBookResponse = {prepareBookResponses: []}
            if (this.services.length) {
                try {
                    prepareBookResponse = await this.$api.prepareBookSaveOrder.post({
                        processIds: this.services.map(s => s.processId.replace(/^.{6}/, '')),
                        checkAvailability: false,
                    })
                } catch (e) {
                    this.prepareBookErrorProcessing(e)
                    this.modal = false
                }
            }
            if (authStore.isTO1) {
                prepareBookResponse.prepareBookResponses.push(
                    ...this.extraServices.map(({salesTerms}) => ({
                        status: 0,
                        currentSalesTerms: salesTerms,
                    }))
                )
            }
            this.prepareBookResponse = prepareBookResponse
            if (!this.services.length && !this.extraServices.length) {
                this.modal = false
                this.$toast.warning(this.$t('unavailable'))
            }
        }

        async confirmExtraServices() {
            const el = document.createElement('div')
            let hasErrors = false
            // const requestPromises = []
            for (const serviceToModify of this.extraServices) {
                // TODO [P4] Need refactoring to use clean logic in composable
                const offlineServiceMixin = useCreateComponent({
                    component: OfflineServiceMixin,
                    props: {
                        serviceToModify,
                        orderTravelers: serviceToModify.travelers,
                        orderProcessId: this.order.processId,
                        clientType: this.order.clientType,
                        customerCompanyId: this.order.customerCompanyId,
                    },
                    el,
                })
                await offlineServiceMixin.reset()
                offlineServiceMixin.tourists = offlineServiceMixin.touristsSelect.map(({value}) => value)
                offlineServiceMixin.status = 'CONFIRMED'
                try {
                    await this.$api.orderServices.put(
                        serviceToModify.processId.replace(/^.{6}/, ''),
                        offlineServiceMixin.requestBody
                    )
                } catch {
                    if (!hasErrors) {
                        this.$toast.error(this.$t('error_message.offline_service_update_error'))
                        hasErrors = true
                    }
                }
            }
            el.remove()
            // TODO Need fix in API api.concurrent.modification.error
            // await Promise.allSettled(requestPromises)
        }

        async continueBooking() {
            if (this.prepareBookResponse.bookingKey) {
                await this.loadPayments()
            } else {
                if (this.extraServices.length) {
                    await this.confirmExtraServices()
                    EventBus.$emit(ORDER_SERVICE_BOOKED)
                }
                this.modal = false
            }
        }

        async loadPayments() {
            try {
                await persistentStore.getPaymentOptions({
                    bookingKey: this.prepareBookResponse.bookingKey,
                    convertToCurrency: persistentStore.getCurrency(this.services[0].salesTerms[0].price.currency),
                })
                this.loaded = true
            } catch (e) {
                this.modal = false
                this.$toast.warning(this.$t('error_message.get_payment_options_unknown_error'))
            }
        }

        async book() {
            this.bookingActive = true
            const paymentMethod = persistentStore.paymentMethods.find(
                paymentMethod => paymentMethod.id === this.paymentMethodId
            )
            if (!paymentMethod.onlinePSP) {
                const success = await this.makeOfflinePayment()
                if (success) {
                    await this.confirmExtraServices()
                    EventBus.$emit(ORDER_SERVICE_BOOKED)
                    this.modal = false
                } else {
                    this.bookingActive = false
                }
            } else {
                await this.confirmExtraServices()
                const success = await this.makeOnlinePayment()
                if (success) {
                    this.modal = false
                } else {
                    this.bookingActive = false
                }
            }
        }

        async makeOfflinePayment() {
            try {
                const answersToQuestionsFiltered = this.answersToQuestions?.filter(a => a.answer?.length)

                await persistentStore.book({
                    bookingKey: this.bookingKey,
                    paymentMethodId: this.paymentMethodId,
                    ...(this.dueToConfirmDateActive && {dueToConfirmDate: this.dueToConfirmDate}),
                    ...(answersToQuestionsFiltered &&
                        answersToQuestionsFiltered.length && {answersToQuestions: answersToQuestionsFiltered}),
                })
                return true
            } catch (e) {
                this.$toast.error(this.$t('error_message.unknown_booking_error'))
                return false
            }
        }

        getPaymentCancelUrl() {
            return window.location.origin + this.$route.fullPath
        }

        get bookingKey() {
            return this.prepareBookResponse?.bookingKey
        }

        get productName() {
            return null
        }

        get corporatePolicyViolationCodeId() {
            return null
        }

        get offersData() {
            return [...this.services, ...this.extraServices].map(
                ({serviceName, supplierCode, serviceType, serviceDetails, processId}) => ({
                    name: serviceName,
                    supplierCode,
                    serviceType,
                    serviceDetails,
                    processId,
                })
            )
        }

        get prepareBookResult() {
            if (this.offersData.length && this.prepareBookResponse) {
                return prepareBookResult(this.prepareBookResponse, this.offersData)
            } else {
                return []
            }
        }
    }

    export default OrderServiceBookModal
</script>

<style scoped lang="scss">
    .v-card-modal .v-card__title {
        background: var(--v-primary-base);
        color: white;
    }
</style>
